import { createPortal } from "react-dom";
import { formatDateTimeShort } from "@/lib/datetime";
import type { AppNotification } from "@/lib/notifications";
import { useDuplicateWaybillStats } from "@/hooks/useDuplicateWaybillStats";

interface DuplicateAlertModalProps {
  alerts: AppNotification[];
  onOpenReport: () => void;
  onClose: () => void;
}

// Tách tên sản phẩm từ nội dung cảnh báo (dạng "(sản phẩm: TÊN).") để hiển thị riêng.
function extractProductName(content: string): string | null {
  const match = content.match(/sản phẩm:\s*([^)]+)\)/i);
  return match ? match[1].trim() : null;
}

// Popup chặn thao tác dành riêng cho admin / quản lý kho: khi phát hiện mã vận
// đơn xuất trùng mới (realtime), popup hiện lên và KHÔNG tự tắt — phải xử lý mới
// thao tác tiếp được. Nhân viên kho không dùng thành phần này nên vẫn làm việc
// bình thường.
export default function DuplicateAlertModal({
  alerts,
  onOpenReport,
  onClose,
}: DuplicateAlertModalProps) {
  const waybills = alerts
    .map((n) => n.ma_van_don)
    .filter((code): code is string => Boolean(code));
  const stats = useDuplicateWaybillStats(waybills);

  if (alerts.length === 0) return null;

  const count = alerts.length;

  return createPortal(
    <div
      className="animate-backdrop-fade fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4"
      role="alertdialog"
      aria-modal="true"
      aria-label="Cảnh báo mã vận đơn xuất trùng"
    >
      <div className="animate-modal-pop flex max-h-[86vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-danger-200 bg-white">
        <div className="flex items-center gap-4 border-b border-danger-100 bg-danger-50 px-6 py-5">
          <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-danger-500 text-white">
            <i className="ri-alarm-warning-line text-2xl" />
            <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-danger-700 px-1 text-[11px] font-bold text-white ring-2 ring-danger-50">
              {count}
            </span>
          </div>
          <h2 className="text-lg font-bold leading-tight text-danger-800">
            Hàng xuất trùng cần nhập lại kho
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Đóng"
            title="Đóng"
            className="ml-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-danger-500 transition-colors hover:bg-white hover:text-danger-800"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <ul className="flex flex-col gap-3">
            {alerts.map((n) => {
              const productName = extractProductName(n.noi_dung);
              const itemStats = n.ma_van_don
                ? stats.get(n.ma_van_don)
                : undefined;
              return (
                <li
                  key={n.id}
                  className="flex items-start gap-4 rounded-xl border border-danger-200 bg-danger-50/50 p-4"
                >
                  <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-danger-100 text-danger-600">
                    <i className="ri-repeat-2-line text-lg" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="text-sm font-bold tracking-tight text-foreground-950">
                        {n.tieu_de}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-danger-100 px-1.5 py-0.5 text-[10px] font-semibold text-danger-700">
                        <i className="ri-error-warning-line" />
                      </span>
                      <span className="ml-auto text-xs font-medium text-foreground-400">
                        {formatDateTimeShort(n.thoi_gian_tao)}
                      </span>
                    </div>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-600">
                      {productName ? (
                        <>
                          Sản phẩm{" "}
                          <span className="font-semibold text-foreground-900">
                            {productName}
                          </span>{" "}
                          — cần kiểm tra lại để tránh sai sót và nhập lại số hàng đã xuất thừa vào kho.
                        </>
                      ) : (
                        "Cần kiểm tra lại để tránh sai sót và nhập lại số hàng đã xuất thừa vào kho."
                      )}
                    </p>
                    {itemStats && (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-1 rounded-full bg-danger-100 px-2.5 py-0.5 text-xs font-semibold text-danger-700">
                          <i className="ri-repeat-line" />
                          Xuất trùng {itemStats.scanCount} lần quét
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-semibold text-accent-900">
                          <i className="ri-archive-2-line" />
                          {itemStats.totalQty} gói xuất thừa
                        </span>
                        {itemStats.employees.length > 0 && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-semibold text-secondary-900">
                            <i className="ri-user-follow-line" />
                            {itemStats.employees.join(", ")}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="border-t border-background-200 px-6 py-4">
          <button
            type="button"
            onClick={onOpenReport}
            className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            <i className="ri-bar-chart-box-line" />
            Tới Báo cáo để xử lý
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}